import React from "react";
import { Box, Typography, Paper, IconButton } from "@mui/material";
import {
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
} from "@mui/icons-material";
import { TextToSpeechButton, RichTextDisplay } from "@/components/common";
import PreviewAudioButton from "@/components/common/PreviewAudioButton";

const arrowSx = {
  position: "absolute",
  top: "200px",
  transform: "translateY(-50%)",
  zIndex: 10,
  bgcolor: "white",
  "&:hover": { bgcolor: "primary.light" },
  "&:disabled": { opacity: 0.3 },
};

const WordCard = ({
  currentWord,
  hasExplanation,
  hasExample,
  canGoNext,
  canGoPrev,
  onNext,
  onPrev,
}) => {
  if (!currentWord) return null;

  return (
    <Paper elevation={3} sx={{ mb: 3, position: "relative", display: "flex", alignItems: "center" }}>
      {/* Left Arrow */}
      <IconButton onClick={onPrev} disabled={!canGoPrev} sx={{ ...arrowSx, left: 8 }}>
        <PrevIcon />
      </IconButton>

      {/* Word Content */}
      <Box sx={{ flex: 1, p: 3, minHeight: "300px" }}>
        {/* Word Image */}
        {currentWord.imageUrl && (
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <img
              src={currentWord.imageUrl}
              alt={currentWord.word}
              style={{
                maxWidth: "100%",
                maxHeight: "200px",
                borderRadius: "8px",
              }}
            />
          </Box>
        )}

        {/* Word Header */}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
          >
            <Typography variant="h4" fontWeight="bold" color="primary">
              {currentWord.word}
            </Typography>
            {
              currentWord.audioUrl ? (
                <PreviewAudioButton audioUrl={currentWord.audioUrl} />
              ) : (
                <TextToSpeechButton text={currentWord.word} size="small" />
              )
            }
          </Box>
          {currentWord.pronunciation && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontStyle: "italic", mt: 0.5 }}
            >
              /{currentWord.pronunciation}/
            </Typography>
          )}
        </Box>

        {/* Translation */}
        {currentWord.translation && (
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              mb: 2,
              color: "primary.main",
              fontWeight: 600,
            }}
          >
            {currentWord.translation}
          </Typography>
        )}

        {/* Explanation */}
        {hasExplanation && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Explanation:
            </Typography>
            <RichTextDisplay content={currentWord.explanation} />
          </Box>
        )}

        {/* Example */}
        {hasExample && (
          <Paper
            elevation={0}
            sx={{
              bgcolor: "grey.50",
              p: 1.5,
              mb: 2,
              borderLeft: 3,
              borderColor: "primary.main",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              gutterBottom
              display="block"
            >
              Example:
            </Typography>
            <RichTextDisplay content={currentWord.example} />
          </Paper>
        )}
      </Box>

      {/* Right Arrow */}
      <IconButton onClick={onNext} disabled={!canGoNext} sx={{ ...arrowSx, right: 8 }}>
        <NextIcon />
      </IconButton>
    </Paper>
  );
};

export default WordCard;
